import * as React from 'react'
import { Copy, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './dropdown-menu'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './context-menu'

/**
 * EditableInfoRow — the editable sibling of {@link InfoRow}. A muted label on
 * the left; on the right the settled value, which edits in place. The whole
 * value is the edit trigger — hovering fills it with a faint gray strip (the
 * affordance), so no persistent pencil is needed and the value stays flush-right
 * and aligned with the read-only rows around it.
 *
 *   text    → a subtle, borderless, line-height-sized input (a light-gray
 *             strip). Commit on Enter / blur; Escape reverts. It never grows the
 *             row height, so the panel doesn't jump as you edit.
 *   select  → an options menu ({@link DropdownMenu} radio group), so enums don't
 *             need an always-on bordered `<Select>` box sitting in every row.
 *
 * `actions` + `copyable` add a right-click {@link ContextMenu} on the value —
 * primary actions (Call, Open, Send email) first, then Copy. This keeps
 * per-value actions off the row (no tiny trailing icons) until summoned.
 *
 * The value truncates and any trailing `adornment` (e.g. a validity Badge) stays
 * pinned — a long email won't overflow or wrap the row.
 */

export interface EditableSelectOption {
  value: string
  label: string
}

export interface EditableRowAction {
  label: string
  icon?: React.ReactNode
  onSelect: () => void
}

export interface EditableInfoRowProps {
  /** The field name — muted, left-aligned. */
  label: React.ReactNode
  /** The current raw value (a string; for selects, the selected option value). */
  value?: string | null
  /** Display node when it differs from `value` (e.g. a select's label). */
  displayValue?: React.ReactNode
  /** Shown muted when there's no value yet. */
  placeholder?: string
  /** Align figures with `tabular-nums`. */
  numeric?: boolean
  /** Editor input type (text mode). Ignored when `options` is set. `date`
   *  renders a native date input (value in `yyyy-MM-dd`); pass a formatted
   *  `displayValue` for read mode. */
  type?: 'text' | 'email' | 'tel' | 'number' | 'date'
  /** When set, the field edits via an options menu instead of a text input. */
  options?: EditableSelectOption[]
  /** Commit handler — fired on Enter / blur (text) or select (menu). */
  onCommit: (next: string) => void
  /**
   * Transform the draft as the user types (text mode) — e.g. a phone mask or
   * digits-only filter. Applied on every change and when seeding the editor
   * from the current value.
   */
  format?: (raw: string) => string
  /**
   * Validate the draft on commit (text mode). Return an error message to block
   * the save (Enter shows it inline; blurring away discards the invalid entry),
   * or `null`/`undefined` to allow it. An empty draft (clearing the field) is
   * never validated.
   */
  validate?: (next: string) => string | null | undefined
  /** Accessible label for the editor + edit trigger. Defaults to `label`. */
  editLabel?: string
  /** Trailing read-mode adornment kept beside the value (e.g. a Badge). */
  adornment?: React.ReactNode
  /** Right-click actions on the value (Call / Open / Send email). */
  actions?: EditableRowAction[]
  /** Add a "Copy {label}" item to the right-click menu. */
  copyable?: boolean
  className?: string
}

const ROW_CLASS = 'group/editrow flex items-start justify-between gap-4 text-sm'
const LABEL_CLASS = 'shrink-0 pt-0.5 text-fbui-gray-500'

export const EditableInfoRow = React.forwardRef<
  HTMLDivElement,
  EditableInfoRowProps
>(
  (
    {
      label,
      value,
      displayValue,
      placeholder = 'Add value',
      numeric,
      type = 'text',
      options,
      onCommit,
      format,
      validate,
      editLabel,
      adornment,
      actions,
      copyable,
      className,
    },
    ref
  ) => {
    const [editing, setEditing] = React.useState(false)
    const [draft, setDraft] = React.useState('')
    const [error, setError] = React.useState<string | null>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)
    // Guards Enter's commit against the blur that fires as the input unmounts.
    const committedRef = React.useRef(false)
    const accessibleLabel =
      editLabel ?? (typeof label === 'string' ? label : 'value')

    // `viaBlur`: clicking away with an invalid draft discards it (reverts to the
    // saved value) rather than trapping focus; Enter surfaces the error inline
    // so the user can fix it. A save is never emitted for an invalid draft.
    const commit = (viaBlur = false) => {
      if (committedRef.current) return
      const next = draft.trim()
      const err = validate && next ? validate(next) : null
      if (err) {
        if (viaBlur) {
          committedRef.current = true
          setError(null)
          setEditing(false)
        } else {
          setError(err)
        }
        return
      }
      committedRef.current = true
      setError(null)
      setEditing(false)
      if ((value ?? '') !== next) onCommit(next)
    }

    const startEditing = () => {
      committedRef.current = false
      setError(null)
      const initial = value ?? ''
      setDraft(format ? format(initial) : initial)
      setEditing(true)
    }

    // Hover fills the value with a faint strip — the edit affordance. `-mx-2 px-2`
    // bleeds the fill past the text without shifting the flush-right alignment.
    const valueClass = cn(
      '-mx-2 min-w-0 truncate rounded px-2 text-right font-medium text-fbui-gray-900',
      'transition-colors group-hover/editrow:bg-fbui-gray-100',
      numeric && 'tabular-nums',
      !value && 'font-normal text-fbui-gray-400'
    )

    // ── Select mode: an options menu, no always-on box. ──────────────────────
    if (options) {
      return (
        <div ref={ref} className={cn(ROW_CLASS, className)}>
          <span className={LABEL_CLASS}>{label}</span>
          <span className="flex min-w-0 items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Edit ${accessibleLabel}`}
                  className={valueClass}
                >
                  <span className="truncate">
                    {displayValue ?? value ?? placeholder}
                  </span>
                </button>
              </DropdownMenuTrigger>
              {/* Cap the list at ~6 rows and scroll — a long option set (e.g. a
                  user picker) shouldn't run the full height of the viewport. */}
              <DropdownMenuContent
                align="end"
                size="sm"
                className="max-h-[13.5rem] overflow-y-auto fbui-scroll"
              >
                <DropdownMenuRadioGroup
                  value={value ?? ''}
                  onValueChange={onCommit}
                >
                  {options.map((o) => (
                    <DropdownMenuRadioItem key={o.value} value={o.value}>
                      {o.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        </div>
      )
    }

    // ── Text mode: a subtle inline strip. ────────────────────────────────────
    const hasMenu = Boolean(value) && Boolean(actions?.length || copyable)

    const valueButton = (
      <button
        type="button"
        aria-label={`Edit ${accessibleLabel}`}
        onClick={startEditing}
        className={valueClass}
      >
        <span className="truncate">
          {value ? (displayValue ?? value) : placeholder}
        </span>
      </button>
    )

    return (
      <div ref={ref} className={cn(ROW_CLASS, className)}>
        <span className={LABEL_CLASS}>{label}</span>
        {editing ? (
          <span className="flex min-w-0 flex-1 flex-col items-end gap-0.5">
            <span className="relative w-full">
              <input
                ref={inputRef}
                /* eslint-disable-next-line jsx-a11y/no-autofocus -- mounts only
                   on explicit click, so focusing immediately is expected. */
                autoFocus
                type={type}
                aria-label={`Edit ${accessibleLabel}`}
                aria-invalid={error ? true : undefined}
                value={draft}
                onChange={(e) => {
                  const raw = e.target.value
                  setDraft(format ? format(raw) : raw)
                  if (error) setError(null)
                }}
                onFocus={(e) => {
                  // `select()` throws (InvalidStateError) on a date input;
                  // only text-like inputs support range selection.
                  if (type !== 'date') e.currentTarget.select()
                }}
                onBlur={() => commit(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commit(false)
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    committedRef.current = true
                    setError(null)
                    setEditing(false)
                  }
                }}
                className={cn(
                  // Light-gray strip: borderless (no ring), line-height-sized
                  // (the negative margin absorbs the padding so the row height
                  // never shifts). Focus is signalled by a darker fill, not a
                  // border — per the "subtle, not bordered" spec. Arbitrary
                  // `px-[0.5rem]` (not `px-2`) so the app's Bootstrap `.px-2`
                  // (!important) can't shadow it and defeat the `pr-*` override.
                  '-my-0.5 w-full min-w-0 rounded border-0 bg-fbui-gray-100 px-[0.5rem] py-0.5',
                  'text-right text-sm text-fbui-gray-900 outline-none',
                  'placeholder:text-fbui-gray-400',
                  'focus:bg-fbui-gray-200',
                  // Make room for the clear × once there's something to clear.
                  // Date inputs carry a native calendar/clear affordance, so we
                  // don't render our own × for them (avoids overlap).
                  draft && type !== 'date' && 'pr-[1.75rem]',
                  // Invalid draft: tint the strip red instead of adding a border.
                  error && 'bg-fbui-red-50 text-fbui-red-700 focus:bg-fbui-red-50'
                )}
                placeholder={placeholder}
              />
              {draft && type !== 'date' && (
                // `onMouseDown` preventDefault keeps the input focused (so the
                // blur-commit doesn't fire); clears the draft in place.
                <button
                  type="button"
                  aria-label={`Clear ${accessibleLabel}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setDraft('')
                    setError(null)
                    inputRef.current?.focus()
                  }}
                  className={cn(
                    'absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full p-0.5 outline-none transition-colors',
                    'text-fbui-gray-500 hover:bg-fbui-gray-300 hover:text-fbui-gray-800',
                    'focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
                  )}
                >
                  <X weight="bold" aria-hidden="true" className="size-3" />
                </button>
              )}
            </span>
            {error && (
              <span role="alert" className="px-0.5 text-xs text-fbui-red-600">
                {error}
              </span>
            )}
          </span>
        ) : (
          <span className="flex min-w-0 items-center justify-end gap-1.5">
            {hasMenu ? (
              <ContextMenu>
                <ContextMenuTrigger asChild>{valueButton}</ContextMenuTrigger>
                <ContextMenuContent>
                  {actions?.map((a) => (
                    <ContextMenuItem
                      key={a.label}
                      icon={a.icon}
                      onSelect={a.onSelect}
                    >
                      {a.label}
                    </ContextMenuItem>
                  ))}
                  {copyable && value ? (
                    <>
                      {actions?.length ? <ContextMenuSeparator /> : null}
                      <ContextMenuItem
                        icon={<Copy />}
                        onSelect={() => navigator.clipboard?.writeText(value)}
                      >
                        Copy {accessibleLabel.toLowerCase()}
                      </ContextMenuItem>
                    </>
                  ) : null}
                </ContextMenuContent>
              </ContextMenu>
            ) : (
              valueButton
            )}
            {adornment}
          </span>
        )}
      </div>
    )
  }
)
EditableInfoRow.displayName = 'EditableInfoRow'
